import { Injectable } from '@angular/core';
import { Route, UrlSegment, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { OAuthService } from '../services/oauth.service';
import { AnalyticsService } from '../services/analytics.service';


@Injectable({
  providedIn: 'root'
})
export class PermissionGuard {

  constructor(
    private oauthService: OAuthService,
    private router: Router,
    private analytics: AnalyticsService
  ) {
    // When permissions change mid-session, re-check the active route tree.
    // If the user lost a required permission, kick them to /unauthorized.
    this.oauthService.permissionsChanged$.subscribe(() => {
      this.recheckActiveRoute();
    });
  }

  canLoad(route: Route, segments: UrlSegment[]): Promise<boolean | UrlTree> {
    const url = '/' + segments.map(s => s.path).join('/');
    return this.checkPermission(route.data?.requiredPermission, url, route.path, route.data?.requiredAllPermissions);
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    return this.checkPermission(route.data?.requiredPermission, state?.url, route.routeConfig?.path, route.data?.requiredAllPermissions);
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    return this.checkPermission(childRoute.data?.requiredPermission, state?.url, childRoute.routeConfig?.path, childRoute.data?.requiredAllPermissions);
  }

  /**
   * `requiredPermission` is an OR-set (any one grants access) — the shape every existing
   * route already uses. `requiredAllPermissions` is an optional AND-set layered on top,
   * for routes whose backend endpoints require a baseline permission (e.g. CanSeeEquipment
   * on every EquipmentReservationsController action) in addition to the OR-set. A route
   * with only an AND-set and no OR-set is valid; one with neither is a config mistake.
   */
  private async checkPermission(
    requiredPermission: string | string[] | undefined | null,
    url?: string,
    routePath?: string,
    requiredAllPermissions?: string[]
  ): Promise<boolean | UrlTree> {
    // Not signed in yet is not the same as signed in and denied — it's "don't know yet," most
    // often a fresh tab/bookmark where the session hasn't loaded, racing this guard against
    // AuthGuard's own sign-in redirect (always present somewhere in this route's ancestor chain
    // — see ROUTE_GUARD_PARENT_CHILD_AUDIT.md). Deferring here, rather than independently denying
    // and redirecting to /unauthorized, removes that race entirely: AuthGuard is the only guard
    // that ever redirects an unauthenticated user, so there is nothing left to race against, and
    // this stops logging a permission_denied event for every anonymous page load.
    if (!(await this.oauthService.isAuthenticated())) {
      return true;
    }

    if (
      (requiredPermission === undefined || requiredPermission === null) &&
      (!requiredAllPermissions || requiredAllPermissions.length === 0)
    ) {
      // A route guarded by PermissionGuard but declaring no requiredPermission would
      // otherwise reach hasPermission(undefined) and throw inside the guard, which the
      // router reports as a failed navigation rather than a denial. Treat it as open and
      // say so - it is a routing configuration mistake, not a user permission problem.
      console.warn(
        `[PermissionGuard] Route "${routePath ?? url}" is guarded but declares no requiredPermission — allowing.`
      );
      return true;
    }

    const anyOfPermissions = requiredPermission
      ? (Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission])
      : [];

    const hasAnyPermission = anyOfPermissions.length === 0
      ? true
      : (await Promise.all(anyOfPermissions.map(permission => this.oauthService.hasPermission(permission))))
        .some(Boolean);

    const allOfPermissions = requiredAllPermissions ?? [];
    const allOfResults = allOfPermissions.length
      ? await Promise.all(allOfPermissions.map(permission => this.oauthService.hasPermission(permission)))
      : [];
    const hasAllRequired = allOfResults.every(Boolean);

    if (hasAnyPermission && hasAllRequired) {
      return true;
    }

    // Denials used to redirect silently, which made "access denied" for a user who
    // plainly holds the rights impossible to diagnose from the console.
    const missing = [
      ...(hasAnyPermission ? [] : [`one of [${anyOfPermissions.join(', ')}]`]),
      ...(hasAllRequired ? [] : [`all of [${allOfPermissions.join(', ')}]`]),
    ];
    console.warn(
      `[PermissionGuard] Denied "${url ?? routePath}" (route "${routePath}") — required:`,
      missing
    );
    this.reportDenial(url, routePath, [...anyOfPermissions, ...allOfPermissions]);
    this.router.navigate(['/unauthorized']);
    return false;
  }

  /**
   * Reports a denial to analytics so gates that reject users who should pass are
   * visible without someone happening to have the console open. AnalyticsService
   * already attaches the signed-in user, the current url and recent breadcrumbs, so
   * this only needs to add which route refused and what it wanted — enough to tell a
   * genuine denial from a permission set that needs correcting.
   */
  private async reportDenial(
    url: string | undefined,
    routePath: string | undefined,
    required: string[]
  ): Promise<void> {
    try {
      const held = await this.oauthService.listPermissions();
      this.analytics.trackAction('permission_denied', 'auth', routePath || url || 'unknown', url, {
        url,
        routePath,
        requiredPermissions: required,
        // Not the user's whole permission set - just whether they hold anything at all,
        // which separates "missing this right" from "permissions failed to load".
        heldPermissionCount: held.length,
      });
    } catch {
      // Never let diagnostics break a navigation decision.
    }
  }

  /**
   * Walks the active route tree and re-checks every `requiredPermission` / `requiredAllPermissions`
   * declared on guarded routes. If any check fails, navigates to /unauthorized.
   */
  private async recheckActiveRoute(): Promise<void> {
    let route = this.router.routerState.snapshot.root;
    while (route) {
      const required = route.data?.requiredPermission;
      const requiredAll: string[] | undefined = route.data?.requiredAllPermissions;
      if (required || requiredAll?.length) {
        const permissions = required ? (Array.isArray(required) ? required : [required]) : [];
        const hasAny = permissions.length === 0
          ? true
          : (await Promise.all(permissions.map(p => this.oauthService.hasPermission(p)))).some(Boolean);
        const allOf = requiredAll ?? [];
        const hasAll = allOf.length === 0
          ? true
          : (await Promise.all(allOf.map(p => this.oauthService.hasPermission(p)))).every(Boolean);
        if (!hasAny || !hasAll) {
          console.warn('[PermissionGuard] Lost access to', this.router.url, '— required:', permissions, requiredAll);
          this.router.navigate(['/unauthorized']);
          return;
        }
      }
      route = route.firstChild as ActivatedRouteSnapshot;
    }
  }
}
